import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AutoComplete, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import type { InputRef } from 'antd';

import theme from '~/theme';
import { Account, Employee, Terminal } from '~/types';
import AccountAutocompleteField from './AccountAutocompleteField';
import { useTranslation } from 'react-i18next';
import {
  EmployeeAccountData,
  EmployeeAccountOption,
  NumberAccountData,
  NumberAccountOption,
} from './AccountOption';
import { debounce } from 'lodash';

interface SectionBarProps {
  title: string;
  className?: string;
}

const SectionBar = ({ title }: SectionBarProps) => {
  return <div className="AccountAutocomplete-SectionBar">{title}</div>;
};

interface AccountAutocompleteProps {
  className?: string;
  onChange?: (account: Account | undefined) => void;
  accountId?: string | null;
  accountFilter?: (account: Account) => boolean;
  disabled?: boolean;
  hiddenAccount?: boolean;
  reset?: boolean;

  loading?: boolean;
  accounts?: Account[];
  terminals?: Terminal[];
  employees?: Employee[];
}

type TerminalMap = {
  [key: string]: Terminal;
};

type AccountMap = {
  [key: string]: Account;
};

const AccountAutocomplete = (props: AccountAutocompleteProps) => {
  const {
    className,
    onChange,
    accountId,
    hiddenAccount,
    disabled,
    accountFilter,
    loading,
    accounts: unfilteredAccounts,
    employees,
    terminals,
    reset,
  } = props;

  const inputRef = useRef<InputRef>(null);

  const [isOpen, setOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [debouncedInputValue, setDebouncedInputValue] = useState<string>('');

  const accounts = useMemo<Account[] | undefined>(() => {
    return accountFilter
      ? unfilteredAccounts?.filter((account) => accountFilter(account))
      : unfilteredAccounts;
  }, [unfilteredAccounts, accountFilter]);

  const accountMap: AccountMap | undefined = useMemo(() => {
    if (accounts) {
      const result: AccountMap = {};
      accounts.forEach((account) => {
        result[account.id!] = account;
      });
      return result;
    } else {
      return undefined;
    }
  }, [accounts]);

  const terminalMap: TerminalMap | undefined = useMemo(() => {
    if (terminals) {
      const result: TerminalMap = {};
      terminals.forEach((terminal) => {
        result[terminal.id!] = terminal;
      });
      return result;
    } else {
      return undefined;
    }
  }, [terminals]);

  const numberAccountData = useMemo<NumberAccountData[]>(() => {
    if (accounts) {
      return accounts.map((account) => {
        return new NumberAccountData(account);
      });
    } else {
      return [];
    }
  }, [accounts]);

  const employeeAccountData = useMemo<EmployeeAccountData[]>(() => {
    if (employees && terminalMap && accountMap) {
      return employees
        .map((employee) => {
          if (employee.terminalId) {
            const terminal = terminalMap[employee.terminalId];
            const accountIds = terminal?.accountIds;
            if (accountIds && accountIds.length > 0) {
              const account = accountMap[accountIds[0]];
              return account ? new EmployeeAccountData(account, employee) : null;
            }
          }
          return null;
        })
        .filter((item) => item !== null) as EmployeeAccountData[];
    } else {
      return [];
    }
  }, [employees, terminalMap, accountMap]);

  const [t] = useTranslation();

  const filteredNumberOptions = useMemo<NumberAccountOption[]>(() => {
    const pattern = (debouncedInputValue || '').toLocaleLowerCase();
    return numberAccountData
      .filter((data) => {
        return data.title.indexOf(pattern) >= 0 || data.number.indexOf(pattern) >= 0;
      })
      .map((data) => new NumberAccountOption(data, pattern));
  }, [numberAccountData, debouncedInputValue]);
  const filteredEmployeeOptions = useMemo<EmployeeAccountOption[]>(() => {
    const pattern = (debouncedInputValue || '').toLocaleLowerCase();
    return employeeAccountData
      .filter((data) => {
        return data.title.indexOf(pattern) >= 0 || data.number.indexOf(pattern) >= 0;
      })
      .map((data) => new EmployeeAccountOption(data, pattern));
  }, [employeeAccountData, debouncedInputValue]);

  const allOptions = useMemo(() => {
    const result = [];
    if (filteredNumberOptions.length > 0) {
      result.push({
        label: <SectionBar title={t('Select account by number')} />,
        options: filteredNumberOptions,
      });
    }
    if (filteredEmployeeOptions.length > 0) {
      result.push({
        label: <SectionBar title={t('Select account by linked employees')} />,
        options: filteredEmployeeOptions,
      });
    }
    return result;
  }, [filteredNumberOptions, filteredEmployeeOptions, t]);

  const debouncedOnChange = debounce(setDebouncedInputValue, 200);

  useEffect(() => {
    const account = accounts?.find((account) => account.number === debouncedInputValue);
    onChange?.(account);
  }, [accounts, debouncedInputValue, onChange]);

  useEffect(() => {
    if (accountId && accountMap?.[accountId]) {
      console.log(`initializing value with provided account ${accountId}`);
      const account = accountMap?.[accountId];
      setInputValue(account.number);
      setDebouncedInputValue(account.number);
    }
  }, [setInputValue, setDebouncedInputValue, accountId, accountMap]);

  useEffect(() => {
    if (reset) {
      console.log(`reset prop is provided; cleaning input`);
      setInputValue('');
      setDebouncedInputValue('');
    }
  }, [setInputValue, setDebouncedInputValue, reset]);

  return isOpen ? (
    <AutoComplete
      dropdownMatchSelectWidth={true}
      options={allOptions}
      popupClassName={className}
      style={{
        width: '100%',
      }}
      disabled={disabled}
      onBlur={(e) => {
        setOpen(false);
        // check if value is correct
        const value: string = (e.target as any).value;
        const account = accounts?.find((account) => account.number === value);
        if (!account) {
          setInputValue('');
          setDebouncedInputValue('');
        }
      }}
      onSelect={(value) => {
        const parts = value.split('||||');
        setInputValue(parts[0]);
        setDebouncedInputValue(parts[0]);
        setOpen(false);
      }}
      value={inputValue}
      onChange={(value) => {
        setInputValue(value);
        // do not debounce if employee option has been selected; onSelect will provide correct value
        if (value.indexOf('||||') < 0) {
          debouncedOnChange(value);
        }
      }}
      defaultOpen
    >
      <Input
        ref={inputRef}
        placeholder={t('Search by account number, account name or employee name')}
        style={{ height: '46px', paddingLeft: 16 }}
        suffix={<SearchOutlined style={{ paddingRight: 4, opacity: 0.5 }} />}
      />
    </AutoComplete>
  ) : (
    <AccountAutocompleteField
      account={
        !hiddenAccount && accounts && accounts.find((account) => account.number === inputValue)
      }
      loading={loading}
      onClick={() => {
        if (!loading && !disabled) {
          setOpen(true);
          setTimeout(() => {
            inputRef.current!.focus({
              cursor: 'end',
            });
          }, 1);
        }
      }}
      disabled={disabled}
    />
  );
};

export default styled(AccountAutocomplete)`
  padding: 0;
  .AccountAutocomplete-Input {
    font-size: 14px;
  }
  .AccountAutocomplete-SectionBar {
    padding: 0;
    font-size: 14px;
    font-weight: bold;
  }
  .ant-select-item-group {
    background-color: ${theme.secondaryBackgroundColor};
    padding-top: 0;
    padding-bottom: 0;
    min-height: 22px;
  }
  .ant-select-item-option {
    padding-left: 16px;
  }
`;
